import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import memberService from '../../services/memberService';
import emailService from '../../services/emailService';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import ProfilePicture from '../../components/common/ProfilePicture';
import {
  Box, Grid, Paper, Typography, TextField, Button, 
  Avatar, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, InputBase,
  Breadcrumbs, CircularProgress, Alert, Divider, useTheme,
  FormControlLabel, Switch, Snackbar,
  IconButton, Popover, Tab, Tabs,
  Tooltip as MuiTooltip,
  ClickAwayListener, Menu, Backdrop, Container,
  FormHelperText
} from '@mui/material';

import {
  Person as PersonIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Palette as PaletteIcon,
  AttachMoney as AttachMoneyIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  MonetizationOn as MonetizationOnIcon,
  LockReset as LockResetIcon,
  ContentCopy as ContentCopyIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
  AdminPanelSettings as AdminIcon,
  Support as SupportIcon
} from '@mui/icons-material';

import { generatePdfFromElement } from '../../utils/pdfUtils';
import TransactionReport from '../../components/reports/TransactionReport';

const MemberDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { user: currentUser } = useAuth(); // Rename to currentUser to avoid conflict
  const theme = useTheme();
  const [member, setMember] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [tenantFields, setTenantFields] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedTheme, setSelectedTheme] = useState(() => {
    return localStorage.getItem('memberCardTheme') || 'default';
  });
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const [debts, setDebts] = useState([]);
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [loadingDebts, setLoadingDebts] = useState(false);
  const [debtPaymentDialog, setDebtPaymentDialog] = useState({
    open: false,
    debt: null
  });
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [resetPasswordDialog, setResetPasswordDialog] = useState({
    open: false,
    loading: false,
    success: false,
    error: null,
    tempPassword: null,
    email: null
  });
  const [passwordVisibility, setPasswordVisibility] = useState(true);
  const [copyTooltip, setCopyTooltip] = useState('Copy to clipboard');
  const transactionReportRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printReportType, setPrintReportType] = useState('all');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [userRole, setUserRole] = useState('');

  const colorThemes = {
    default: {
      name: 'Default Blue',
      gradient: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
      accent: '#3f51b5',
      accentLight: 'rgba(63, 81, 181, 0.2)'
    },
    emerald: {
      name: 'Emerald Green',
      gradient: 'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
      accent: '#2E7D32',
      accentLight: 'rgba(46, 125, 50, 0.2)'
    },
    sunset: {
      name: 'Sunset Orange',
      gradient: 'linear-gradient(135deg, #E64A19 0%, #FF7043 100%)',
      accent: '#E64A19',
      accentLight: 'rgba(230, 74, 25, 0.2)'
    },
    royal: {
      name: 'Royal Purple',
      gradient: 'linear-gradient(135deg, #512DA8 0%, #7E57C2 100%)',
      accent: '#512DA8',
      accentLight: 'rgba(81, 45, 168, 0.2)'
    },
    midnight: {
      name: 'Midnight Blue',
      gradient: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
      accent: '#1A237E',
      accentLight: 'rgba(26, 35, 126, 0.2)'
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    mode: 'onBlur'
  });

  const paymentForm = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: '',
      method: 'credit_card',
      description: ''
    }
  });

  const debtForm = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: '',
      category: 'dues',
      dueDate: new Date().toISOString().split('T')[0],
      description: ''
    }
  });

  const debtPaymentForm = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: '',
      method: 'credit_card',
      description: ''
    }
  });

  const transactionForm = useForm({
    mode: 'onBlur',
    defaultValues: {
      amount: '',
      reason: 'refund',
      description: ''
    }
  });

  useEffect(() => {
    const fetchMember = async () => {
      try {
        setIsLoading(true);
        const response = await memberService.getMember(id);
        let memberData;
        if (response.data && response.data.data) {
          memberData = response.data.data;
        } else if (response.data) {
          memberData = response.data;
        } else {
          memberData = response;
        }
        if (!memberData) {
          throw new Error('No member data found in server response');
        }
        setMember(memberData);

        const userRoleFromData = memberData.userId?.role || memberData.role || 'member';
        setUserRole(userRoleFromData);

        reset({
          membershipType: memberData.membershipType,
          status: memberData.status,
          notes: memberData.notes || '',
          customFields: memberData.customFields || {},
          role: userRoleFromData
        });
      } catch (error) {
        setError(error.response?.data?.message || error.message || 'Failed to load member data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchMember();
  }, [id, reset]);

  useEffect(() => {
    if (tenant && tenant.settings && tenant.settings.membershipFields) {
      setTenantFields(tenant.settings.membershipFields);
    }
  }, [tenant]);

  useEffect(() => {
    const savedTheme = localStorage.getItem('memberCardTheme');
    if (savedTheme && colorThemes[savedTheme]) {
      setSelectedTheme(savedTheme);
    }
  }, []);

  const fetchMemberDebts = async () => {
    if (!id) return;
    
    try {
      setLoadingDebts(true);
      console.log(`Fetching debts for member ID: ${id}`);
      
      // First verify the member exists to avoid 500 errors
      await memberService.getMember(id);
      
      // Make the actual debt request with proper error handling
      try {
        const response = await memberService.getMemberDebts(id);
        console.log('Member debts API response:', response);
        
        let debtsData = [];
        
        // Handle different possible response structures
        if (response && response.data && Array.isArray(response.data)) {
          debtsData = response.data;
        } else if (response && response.data && response.data.debts && Array.isArray(response.data.debts)) {
          debtsData = response.data.debts;
        } else if (Array.isArray(response)) {
          debtsData = response;
        } else if (response && typeof response === 'object') {
          // If response is an object but none of the above structures match
          console.log('Unexpected response structure for debts:', response);
          debtsData = [];
        }
        
        console.log(`Successfully loaded ${debtsData.length} debts for member`);
        setDebts(debtsData);
      } catch (debtError) {
        console.error('Error in debts API call:', debtError);
        
        // Log detailed information about the error
        if (debtError.response) {
          console.error('Server response:', {
            status: debtError.response.status,
            data: debtError.response.data,
            headers: debtError.response.headers
          });
        } else if (debtError.request) {
          console.error('No response received:', debtError.request);
        } else {
          console.error('Error setting up request:', debtError.message);
        }
        
        // Set empty debts array instead of crashing with error
        setDebts([]);
        
        // Show user-friendly error message
        setSnackbar({
          open: true,
          message: 'Unable to load debts information. This issue has been logged.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error in fetchMemberDebts:', error);
      
      // Set empty debts as fallback
      setDebts([]);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: 'Failed to load member debts: ' + (error.response?.data?.message || error.message || 'Unknown server error'),
        severity: 'error'
      });
    } finally {
      setLoadingDebts(false);
    }
  };

  const fetchMemberTransactions = async () => {
    if (!id) return;
    try {
      setLoadingTransactions(true);
      const response = await memberService.getMemberTransactions(id);
      let transactionsData = [];
      if (response.data && Array.isArray(response.data)) {
        transactionsData = response.data;
      } else if (response.data && response.data.transactions && Array.isArray(response.data.transactions)) {
        transactionsData = response.data.transactions;
      } else if (Array.isArray(response)) {
        transactionsData = response;
      }
      setTransactions(transactionsData);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to load member transactions: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    const fetchMember = async () => {
      await fetchMemberDebts();
      await fetchMemberTransactions();
    };

    fetchMember();
  }, [id, reset]);

  const onSubmit = async (data) => {
    try {
      setIsSaving(true);
      setError(null);
      if (data.directoryVisible !== undefined) {
        data.directoryVisible = data.directoryVisible === true || data.directoryVisible === "true";
      }
      
      // Add role validation - prevent non-admins from setting staff role
      if (data.role === 'staff' && 
          currentUser?.role !== 'admin' && 
          currentUser?.role !== 'superadmin') {
        setError('You do not have permission to assign staff roles');
        setIsSaving(false);
        return;
      }
      
      // Ensure we have the role in our update data
      // This is critical - we need to ensure the role is explicitly sent to the API
      const cleanedData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {});
      
      // If the form includes a role field, make sure it's properly included
      if (data.role) {
        console.log(`Updating member role to: ${data.role}`);
        
        // The userId may be an object or just the ID depending on the API
        if (member.userId && typeof member.userId === 'object') {
          // We need to update the user's role, not just the member's role
          cleanedData.userRole = data.role;
        } else {
          // Direct role assignment if userId is just an ID
          cleanedData.role = data.role;
        }
      }
      
      const response = await memberService.updateMember(id, cleanedData);
      if (response && response.data && response.data.data) {
        setMember(response.data.data);
      } else if (response && response.data) {
        setMember(response.data);
      } else if (response && response.success && response.data) {
        setMember(response.data);
      } else {
        const refreshResponse = await memberService.getMember(id);
        if (refreshResponse && refreshResponse.data) {
          setMember(refreshResponse.data);
        }
      }
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Member details updated successfully',
        severity: 'success'
      });

      // Send email notification to member about account updates
      if (member?.userId?.email) {
        try {
          const memberEmail = member.userId.email;
          const memberName = `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim();
          const statusChanged = data.status && data.status !== member.status;
          const membershipTypeChanged = data.membershipType && data.membershipType !== member.membershipType;
          
          // Create list of changed fields for email
          const changedFields = [];
          if (statusChanged) changedFields.push({ label: 'Status', value: data.status });
          if (membershipTypeChanged) changedFields.push({ label: 'Membership Type', value: data.membershipType });
          if (data.customFields) {
            Object.entries(data.customFields).forEach(([key, value]) => {
              if (value !== member.customFields?.[key]) {
                changedFields.push({ 
                  label: tenantFields.find(f => f.name === key)?.label || key, 
                  value: value 
                });
              }
            });
          }
          
          const notificationData = {
            to: memberEmail,
            notificationType: 'account_updated',
            memberName: memberName,
            organizationName: tenant?.name || 'Organization',
            details: {
              message: `An administrator has updated your account information.`,
              itemsTitle: 'Updated Information',
              items: changedFields
            },
            callToAction: {
              text: 'View Your Account',
              url: `${window.location.origin}/member/profile`
            }
          };
          
          await emailService.sendMemberNotification(notificationData);
          console.log('Account update notification sent to member');
        } catch (emailError) {
          console.error('Failed to send account update notification:', emailError);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to update member');
      setSnackbar({
        open: true,
        message: 'Failed to update member: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePaymentSubmit = async (data) => {
    try {
      setIsSaving(true);
      setError(null);
      const paymentData = {
        ...data,
        amount: parseFloat(data.amount),
        date: new Date().toISOString()
      };
      await memberService.addPayment(id, paymentData);
      setPaymentModalOpen(false);
      paymentForm.reset();
      setSnackbar({
        open: true,
        message: `Payment of $${paymentData.amount.toFixed(2)} recorded successfully`,
        severity: 'success'
      });
      const refreshResponse = await memberService.getMember(id);
      if (refreshResponse.data && refreshResponse.data.data) {
        setMember(refreshResponse.data.data);
      } else if (refreshResponse.data) {
        setMember(refreshResponse.data);
      } else {
        setMember(refreshResponse);
      }

      // Send email notification about the payment
      if (member?.userId?.email) {
        try {
          const memberEmail = member.userId.email;
          const memberName = `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim();
          
          const notificationData = {
            to: memberEmail,
            notificationType: 'payment_recorded',
            memberName: memberName,
            organizationName: tenant?.name || 'Organization',
            details: {
              message: `A payment has been recorded to your account.`,
              itemsTitle: 'Payment Details',
              items: [
                { label: 'Amount', value: `$${paymentData.amount.toFixed(2)}` },
                { label: 'Method', value: paymentData.method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) },
                { label: 'Date', value: new Date(paymentData.date).toLocaleDateString() },
                { label: 'Description', value: paymentData.description || 'Not specified' }
              ]
            },
            callToAction: {
              text: 'View Your Account',
              url: `${window.location.origin}/member/profile`
            }
          };
          
          await emailService.sendMemberNotification(notificationData);
          console.log('Payment notification sent to member');
        } catch (emailError) {
          console.error('Failed to send payment notification:', emailError);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add payment');
      setSnackbar({
        open: true,
        message: 'Failed to add payment: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDebtSubmit = async (data) => {
    try {
      setIsSaving(true);
      setError(null);
      const debtData = {
        ...data,
        amount: parseFloat(data.amount),
        status: 'active'
      };
      const response = await memberService.addDebt(id, debtData);
      setDebtDialogOpen(false);
      debtForm.reset();
      await fetchMemberDebts();
      const memberResponse = await memberService.getMember(id);
      if (memberResponse.data && memberResponse.data.data) {
        setMember(memberResponse.data.data);
      } else if (memberResponse.data) {
        setMember(memberResponse.data);
      } else {
        setMember(memberResponse);
      }
      
      setSnackbar({
        open: true,
        message: `Debt of $${debtData.amount.toFixed(2)} added successfully`,
        severity: 'success'
      });
      
      // Send email notification about the new debt
      if (member?.userId?.email) {
        try {
          const memberEmail = member.userId.email;
          const memberName = `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim();
          
          const notificationData = {
            to: memberEmail,
            notificationType: 'debt_added',
            memberName: memberName,
            organizationName: tenant?.name || 'Organization',
            details: {
              message: `A new financial obligation has been added to your account.`,
              itemsTitle: 'Debt Details',
              items: [
                { label: 'Amount', value: `$${debtData.amount.toFixed(2)}` },
                { label: 'Category', value: debtData.category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) },
                { label: 'Due Date', value: new Date(debtData.dueDate).toLocaleDateString() },
                { label: 'Description', value: debtData.description || 'Not specified' }
              ]
            },
            callToAction: {
              text: 'View Your Account',
              url: `${window.location.origin}/member/profile`
            }
          };
          
          await emailService.sendMemberNotification(notificationData);
          console.log('Debt notification sent to member');
        } catch (emailError) {
          console.error('Failed to send debt notification:', emailError);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add debt');
      setSnackbar({
        open: true,
        message: 'Failed to add debt: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDebt = async (debtId) => {
    if (!window.confirm('Are you sure you want to delete this debt?')) return;
    try {
      await memberService.deleteDebt(id, debtId);
      await fetchMemberDebts();
      setSnackbar({
        open: true,
        message: 'Debt deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete debt: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  const handleToggleDebtStatus = async (debtId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'resolved' : 'active';
    try {
      await memberService.updateDebtStatus(id, debtId, newStatus);
      await fetchMemberDebts();
      setSnackbar({
        open: true,
        message: `Debt marked as ${newStatus}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update debt status: ' + (error.message || 'Unknown error'),
        severity: 'error'
      });
    }
  };

  const handleDebtPaymentSubmit = async (data) => {
    try {
      setIsSaving(true);
      setError(null);
      if (!debtPaymentDialog.debt) {
        throw new Error('No debt selected for payment');
      }
      const paymentData = {
        ...data,
        amount: parseFloat(data.amount),
        date: new Date().toISOString()
      };
      await memberService.applyPaymentToDebt(id, paymentData, debtPaymentDialog.debt._id);
      setDebtPaymentDialog({ open: false, debt: null });
      debtPaymentForm.reset();
      setSnackbar({
        open: true,
        message: `Payment of $${paymentData.amount.toFixed(2)} applied to debt successfully`,
        severity: 'success'
      });
      const refreshResponse = await memberService.getMember(id);
      if (refreshResponse.data && refreshResponse.data.data) {
        setMember(refreshResponse.data.data);
      } else if (refreshResponse.data) {
        setMember(refreshResponse.data);
      } else {
        setMember(refreshResponse);
      }
      await fetchMemberDebts();
      
      // Send email notification about the debt payment
      if (member?.userId?.email) {
        try {
          const memberEmail = member.userId.email;
          const memberName = `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim();
          
          const notificationData = {
            to: memberEmail,
            notificationType: 'payment_recorded',
            memberName: memberName,
            organizationName: tenant?.name || 'Organization',
            details: {
              message: `A payment has been applied to your ${debtPaymentDialog.debt.category.replace(/_/g, ' ')} debt.`,
              itemsTitle: 'Payment Details',
              items: [
                { label: 'Amount', value: `$${paymentData.amount.toFixed(2)}` },
                { label: 'Method', value: paymentData.method.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) },
                { label: 'Date', value: new Date(paymentData.date).toLocaleDateString() },
                { 
                  label: 'Remaining Balance', 
                  value: `$${Math.max(0, (debtPaymentDialog.debt.amount - paymentData.amount)).toFixed(2)}` 
                },
                { label: 'Description', value: paymentData.description || 'Not specified' }
              ]
            },
            callToAction: {
              text: 'View Your Account',
              url: `${window.location.origin}/member/profile`
            }
          };
          
          await emailService.sendMemberNotification(notificationData);
          console.log('Debt payment notification sent to member');
        } catch (emailError) {
          console.error('Failed to send debt payment notification:', emailError);
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to apply payment: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleTransactionSubmit = async (data) => {
    try {
      setIsSaving(true);
      setError(null);
      const transactionData = {
        ...data,
        amount: parseFloat(data.amount),
        date: new Date().toISOString()
      };
      const response = await memberService.sendMoneyToMember(id, transactionData);
      setTransactionDialogOpen(false);
      transactionForm.reset();
      await fetchMemberTransactions();
      setSnackbar({
        open: true,
        message: `${formatCurrency(transactionData.amount)} sent to member successfully`,
        severity: 'success'
      });
      const refreshResponse = await memberService.getMember(id);
      if (refreshResponse.data && refreshResponse.data.data) {
        setMember(refreshResponse.data.data);
      } else if (refreshResponse.data) {
        setMember(refreshResponse.data);
      } else {
        setMember(refreshResponse);
      }
      
      // Send email notification about the money sent
      if (member?.userId?.email) {
        try {
          const memberEmail = member.userId.email;
          const memberName = `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim();
          
          const notificationData = {
            to: memberEmail,
            notificationType: 'payment_sent',
            memberName: memberName,
            organizationName: tenant?.name || 'Organization',
            details: {
              message: `We're pleased to inform you that a payment has been sent to your account.`,
              itemsTitle: 'Transaction Details',
              items: [
                { label: 'Amount', value: `$${transactionData.amount.toFixed(2)}` },
                { label: 'Reason', value: transactionData.reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) },
                { label: 'Date', value: new Date(transactionData.date).toLocaleDateString() },
                { label: 'Description', value: transactionData.description || 'Not specified' }
              ]
            },
            callToAction: {
              text: 'View Your Account',
              url: `${window.location.origin}/member/profile`
            }
          };
          
          await emailService.sendMemberNotification(notificationData);
          console.log('Transaction notification sent to member');
        } catch (emailError) {
          console.error('Failed to send transaction notification:', emailError);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to send money');
      setSnackbar({
        open: true,
        message: 'Failed to send money: ' + (error.response?.data?.message || error.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenDebtPaymentDialog = (debt) => {
    debtPaymentForm.setValue('amount', debt.amount);
    debtPaymentForm.setValue('description', `Payment for ${debt.category.replace(/_/g, ' ')}`);
    setDebtPaymentDialog({
      open: true,
      debt: debt
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({...snackbar, open: false});
  };

  const handleThemeChange = (themeKey) => {
    setSelectedTheme(themeKey);
    localStorage.setItem('memberCardTheme', themeKey);
    setColorMenuAnchor(null);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleResetPassword = async () => {
    try {
      setResetPasswordDialog(prev => ({ ...prev, loading: true, error: null }));
      const response = await memberService.resetMemberPassword(id);
      
      const tempPassword = response.data?.tempPassword;
      const email = response.data?.email || member?.userId?.email;
      
      setResetPasswordDialog(prev => ({ 
        ...prev, 
        loading: false, 
        success: true,
        error: null,
        tempPassword,
        email
      }));
      
      setSnackbar({
        open: true,
        message: 'Password reset successfully. A new password has been sent to the member\'s email.',
        severity: 'success'
      });
      
      // Send email notification about password reset
      if (email) {
        try {
          const memberName = `${member.userId.firstName || ''} ${member.userId.lastName || ''}`.trim();
          
          const notificationData = {
            to: email,
            notificationType: 'password_reset',
            memberName: memberName,
            organizationName: tenant?.name || 'Organization',
            details: {
              message: 'Your account password has been reset by an administrator.',
              itemsTitle: 'Important Information',
              items: [
                { 
                  label: 'Action Required', 
                  value: 'Please use the temporary password sent to your email to log in, then change your password immediately.' 
                },
                { 
                  label: 'Reset Time', 
                  value: new Date().toLocaleString() 
                }
              ]
            },
            callToAction: {
              text: 'Login Now',
              url: `${window.location.origin}/login`
            }
          };
          
          await emailService.sendMemberNotification(notificationData);
          console.log('Password reset notification sent to member');
        } catch (emailError) {
          console.error('Failed to send password reset notification:', emailError);
        }
      }
    } catch (error) {
      setResetPasswordDialog(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Failed to reset password'
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisibility(!passwordVisibility);
  };
  
  const copyPasswordToClipboard = () => {
    if (resetPasswordDialog.tempPassword) {
      navigator.clipboard.writeText(resetPasswordDialog.tempPassword)
        .then(() => {
          setCopyTooltip('Copied!');
          setTimeout(() => {
            setCopyTooltip('Copy to clipboard');
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy password: ', err);
          setCopyTooltip('Failed to copy');
        });
    }
  };

  const handleCloseResetDialog = () => {
    setResetPasswordDialog({
      open: false,
      loading: false,
      success: false,
      error: null,
      tempPassword: null,
      email: null
    });
  };

  const handlePrintMenuOpen = (event) => {
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSelectPrintReport = (type) => {
    setPrintReportType(type);
    setPrintDialogOpen(true);
    handlePrintMenuClose();
  };

  const handlePrintReport = async () => {
    try {
      setIsPrinting(true);
      
      if (!transactionReportRef.current) {
        throw new Error('Report component not found');
      }

      const fileName = `${member?.userId?.lastName}_${member?.userId?.firstName}_${printReportType}_report`;
      
      await generatePdfFromElement(
        transactionReportRef.current,
        fileName,
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setPrintDialogOpen(false);
            setSnackbar({
              open: true,
              message: 'Financial report generated successfully!',
              severity: 'success'
            });
          },
          onError: (error) => {
            console.error('PDF generation error:', error);
            setIsPrinting(false);
            setSnackbar({
              open: true,
              message: 'Failed to generate financial report: ' + error.message,
              severity: 'error'
            });
          }
        }
      );
    } catch (error) {
      console.error('Error printing report:', error);
      setIsPrinting(false);
      setSnackbar({
        open: true,
        message: 'Failed to generate financial report: ' + error.message,
        severity: 'error'
      });
    }
  };

  const constructProfilePictureUrl = (profilePicturePath) => {
    if (!profilePicturePath) return null;
    
    try {
      if (profilePicturePath.startsWith('http')) {
        return profilePicturePath;
      }
      
      let apiBaseUrl = import.meta.env.VITE_API_URL || '';
      
      if (!apiBaseUrl) apiBaseUrl = 'http://localhost:5000';
      
      apiBaseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl;
      
      apiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
      
      let normalizedPath = profilePicturePath;
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = '/' + normalizedPath;
      }
      
      if (normalizedPath.includes('/uploads/profile-pictures/')) {
        return `${apiBaseUrl}${normalizedPath}`;
      } else if (normalizedPath.includes('/uploads/')) {
        return `${apiBaseUrl}${normalizedPath}`;
      } else {
        normalizedPath = normalizedPath.replace(/^\//, '');
        return `${apiBaseUrl}/uploads/profile-pictures/${normalizedPath}`;
      }
    } catch (e) {
      console.error('Error constructing profile picture URL:', e);
      return null;
    }
  };

  const totalActiveDebts = debts
    .filter(debt => debt.status === 'active')
    .reduce((sum, debt) => sum + (debt.amount || 0), 0);

  const getBackPath = () => {
    if (currentUser?.role === 'staff') {
      return '/staff/members';
    } else if (currentUser?.role === 'admin' || currentUser?.role === 'superadmin') {
      return '/admin/members';
    } else {
      return '/member/dashboard';
    }
  };

  if (isLoading) {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, md: 4 }, 
          mb: 4,
          px: { xs: 2, md: 3 }
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !member) {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, md: 4 }, 
          mb: 4,
          px: { xs: 2, md: 3 }
        }}
      >
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button 
          onClick={() => navigate(getBackPath())}
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{
            background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
          }}
        >
          Back to members
        </Button>
      </Container>
    );
  }

  if (!member) {
    return (
      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, md: 4 }, 
          mb: 4,
          px: { xs: 2, md: 3 }
        }}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <Typography variant="h5" color="textSecondary">
            Member not found
          </Typography>
        </Box>
        <Button 
          onClick={() => navigate(getBackPath())}
          variant="contained"
          startIcon={<ArrowBackIcon />}
          sx={{
            background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
          }}
        >
          Back to members
        </Button>
      </Container>
    );
  }

  const userData = member.userId;
  const hasProfilePicture = !!(userData?.profilePicture || member.profilePicture);
  const profilePictureUrl = hasProfilePicture 
    ? constructProfilePictureUrl(userData?.profilePicture || member.profilePicture)
    : null;

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 }
      }}
    >
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Breadcrumbs 
              aria-label="breadcrumb" 
              sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}
            >
              <Button onClick={() => navigate(getBackPath())} style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>
                Members
              </Button>
              <Typography color="white">Member Details</Typography>
            </Breadcrumbs>
            <Typography variant="h4" sx={{ fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} /> {userData?.firstName} {userData?.lastName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Chip 
                label={member.status ? member.status.toUpperCase() : 'UNKNOWN'}
                size="small"
                sx={{ 
                  bgcolor: member.status === 'active' ? 'rgba(76, 175, 80, 0.3)' : 
                          member.status === 'pending' ? 'rgba(255, 152, 0, 0.3)' : 
                          'rgba(244, 67, 54, 0.3)',
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
              {(() => {
                const userRole = member.userId?.role || member.role || 'member';
                let RoleIcon, roleColor, roleLabel;
                
                switch(userRole) {
                  case 'admin':
                    RoleIcon = AdminIcon;
                    roleColor = '#fff';
                    roleLabel = 'Admin';
                    break;
                  case 'staff':
                    RoleIcon = SupportIcon;
                    roleColor = 'rgba(255, 255, 255, 0.9)';
                    roleLabel = 'Staff';
                    break;
                  case 'superadmin':
                    RoleIcon = AdminIcon;
                    roleColor = '#fff';
                    roleLabel = 'Superadmin';
                    break;
                  default:
                    RoleIcon = PersonIcon;
                    roleColor = 'rgba(255, 255, 255, 0.7)';
                    roleLabel = 'Member';
                }
                
                return userRole !== 'member' ? (
                  <Chip 
                    icon={<RoleIcon fontSize="small" />}
                    label={roleLabel}
                    sx={{ 
                      ml: 2, 
                      color: roleColor,
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      fontWeight: 'bold',
                      border: `1px solid rgba(255, 255, 255, 0.3)`,
                    }}
                  />
                ) : null;
              })()}
            </Box>
          </Box>

          <Box>
            <Button
              onClick={() => navigate(getBackPath())}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                mr: 2,
                color: 'white', 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Back to Members
            </Button>
            
            <Button
              onClick={handlePrintMenuOpen}
              variant="outlined"
              startIcon={<PrintIcon />}
              sx={{ 
                mr: 2,
                color: 'white', 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Print Report
            </Button>
            
            {!isEditing ? (
              <Button
                onClick={() => setIsEditing(true)}
                variant="contained"
                startIcon={<EditIcon />}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                Edit Member
              </Button>
            ) : (
              <Button
                onClick={() => setIsEditing(false)}
                variant="outlined"
                sx={{ 
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={3} 
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              height: '100%',
              background: colorThemes[selectedTheme].gradient,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h100v100H0z" fill="none"/%3E%3Cpath d="M0 0h100v100H0z" fill="rgba(255,255,255,0.05)"/%3E%3C/svg%3E")',
                opacity: 0.1,
                zIndex: 0
              }
            }}
          >
            {!isEditing ? (
              <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: 'white',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: 1
                    }}
                  >
                    {tenant?.name || 'MEMBERSHIP CARD'}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <MuiTooltip title="Change card theme">
                      <IconButton
                        onClick={(e) => setColorMenuAnchor(e.currentTarget)}
                        sx={{ color: 'white' }}
                      >
                        <PaletteIcon />
                      </IconButton>
                    </MuiTooltip>
                    <Chip 
                      label={member.membershipType?.toUpperCase() || 'STANDARD'} 
                      sx={{ 
                        bgcolor: colorThemes[selectedTheme].accentLight,
                        color: 'white',
                        fontWeight: 'bold',
                        backdropFilter: 'blur(4px)'
                      }} 
                    />
                  </Box>
                </Box>

                <Popover
                  open={Boolean(colorMenuAnchor)}
                  anchorEl={colorMenuAnchor}
                  onClose={() => setColorMenuAnchor(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: { 
                      mt: 1,
                      borderRadius: 2,
                      boxShadow: 3,
                      overflow: 'hidden'
                    }
                  }}
                >
                  <Box sx={{ p: 1 }}>
                    {Object.entries(colorThemes).map(([key, theme]) => (
                      <Button
                        key={key}
                        fullWidth
                        onClick={() => handleThemeChange(key)}
                        sx={{
                          justifyContent: 'flex-start',
                          px: 2,
                          py: 1,
                          mb: 0.5,
                          background: theme.gradient,
                          color: 'white',
                          '&:hover': {
                            background: theme.gradient,
                            opacity: 0.9
                          }
                        }}
                      >
                        {theme.name}
                      </Button>
                    ))}
                  </Box>
                </Popover>

                <Box 
                  sx={{
                    position: 'absolute',
                    right: 20,
                    top: 70,
                    width: 60,
                    height: 45,
                    opacity: 0.7,
                    background: 'radial-gradient(circle at 0 0, transparent 40%, rgba(255,255,255,0.1) 40%)',
                    borderRadius: '8px'
                  }}
                />

                <Box sx={{ mb: 4 }}>
                  {hasProfilePicture ? (
                    <Avatar
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        mb: 2,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        fontSize: '2rem',
                        border: '3px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      <ProfilePicture 
                        src={profilePictureUrl}
                        firstName={userData?.firstName || ''}
                        lastName={userData?.lastName || ''}
                        size="xl"
                        round={true}
                      />
                    </Avatar>
                  ) : (
                    <Avatar
                      sx={{ 
                        width: 80, 
                        height: 80, 
                        mb: 2,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        fontSize: '2rem',
                        border: '3px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      {userData?.firstName?.[0]}{userData?.lastName?.[0]}
                    </Avatar>
                  )}
                  <Typography variant="h4" sx={{ color: 'white', mb: 1, fontWeight: 'medium' }}>
                    {userData?.firstName} {userData?.lastName}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
                    {userData?.email}
                  </Typography>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Member Since
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {formatDate(member.joinDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Status
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'white', textTransform: 'capitalize' }}>
                      {member.status || 'Active'}
                    </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ 
                  mt: 'auto', 
                  pt: 3,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Membership ID
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white',
                      fontFamily: 'monospace',
                      letterSpacing: 2
                    }}
                  >
                    {member.membershipNumber}
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 3,
                  pt: 2,
                  borderTop: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Button
                    onClick={() => setResetPasswordDialog({ ...resetPasswordDialog, open: true })}
                    variant="contained"
                    startIcon={<LockResetIcon />}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                      fontWeight: 'medium'
                    }}
                  >
                    Reset Password
                  </Button>
                </Box>

                <Button
                  onClick={() => setIsEditing(true)}
                  variant="contained"
                  startIcon={<EditIcon />}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.3)',
                    }
                  }}
                >
                  Edit
                </Button>
              </Box>
            ) : (
              <Box 
                component="form" 
                noValidate
                id="member-edit-form"
                onSubmit={handleSubmit(onSubmit)} 
                sx={{ 
                  p: 3, 
                  position: 'relative', 
                  zIndex: 1,
                  color: 'white' 
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }} id="role-label">
                        User Role
                      </InputLabel>
                      <Select
                        labelId="role-label"
                        id="role"
                        label="User Role"
                        defaultValue={userRole || 'member'}
                        {...register('role')}
                        sx={{
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.6)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.9)',
                          },
                          '.MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {selected === 'admin' && <AdminIcon fontSize="small" />}
                            {selected === 'staff' && <SupportIcon fontSize="small" />}
                            {selected === 'superadmin' && <AdminIcon fontSize="small" />}
                            {selected === 'member' && <PersonIcon fontSize="small" />}
                            {selected}
                          </Box>
                        )}
                      >
                        <MenuItem value="member">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography>Member</Typography>
                          </Box>
                        </MenuItem>
                        {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                          <MenuItem value="staff">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <SupportIcon fontSize="small" color="secondary" />
                              <Typography>Staff</Typography>
                            </Box>
                          </MenuItem>
                        )}
                        {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                          <MenuItem value="admin">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AdminIcon fontSize="small" color="primary" />
                              <Typography>Admin</Typography>
                            </Box>
                          </MenuItem>
                        )}
                        {currentUser?.role === 'superadmin' && (
                          <MenuItem value="superadmin">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AdminIcon fontSize="small" color="error" />
                              <Typography>Superadmin</Typography>
                            </Box>
                          </MenuItem>
                        )}
                      </Select>
                      {currentUser?.role !== 'admin' && currentUser?.role !== 'superadmin' && (
                        <FormHelperText sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          Only administrators can change user roles to staff
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }} id="membership-type-label">
                        Membership Type
                      </InputLabel>
                      <Select
                        labelId="membership-type-label"
                        id="membershipType"
                        label="Membership Type"
                        defaultValue={member.membershipType || 'regular'}
                        {...register('membershipType')}
                        sx={{
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.6)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.9)',
                          },
                          '.MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }}
                      >
                        <MenuItem value="regular">Regular</MenuItem>
                        <MenuItem value="premium">Premium</MenuItem>
                        <MenuItem value="lifetime">Lifetime</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }} id="status-label">Status</InputLabel>
                      <Select
                        labelId="status-label"
                        id="status"
                        label="Status"
                        defaultValue={member.status}
                        {...register('status')}
                        sx={{
                          color: 'white',
                          '.MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.6)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.9)',
                          },
                          '.MuiSvgIcon-root': {
                            color: 'white',
                          }
                        }}
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="suspended">Suspended</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {tenantFields.map(field => (
                    <Grid item xs={12} key={field.name}>
                      <TextField
                        fullWidth
                        label={field.label || field.name.charAt(0).toUpperCase() + field.name.slice(1).replace(/([A-Z])/g, ' $1')}
                        defaultValue={member.customFields?.[field.name] || ''}
                        {...register(`customFields.${field.name}`)}
                        sx={{
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255,255,255,0.7)',
                          },
                          '& .MuiInputBase-input': {
                            color: 'white',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.6)',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.9)',
                          }
                        }}
                      />
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={4}
                      defaultValue={member.notes || ''}
                      {...register('notes')}
                      sx={{
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                        },
                        '& .MuiInputBase-input': {
                          color: 'white',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.3)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.6)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255,255,255,0.9)',
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
                    <FormControlLabel
                      control={
                        <Switch
                          defaultChecked={member.directoryVisible !== false}
                          {...register('directoryVisible')}
                          sx={{
                            '& .MuiSwitch-track': {
                              backgroundColor: 'rgba(255,255,255,0.3)',
                            },
                            '& .MuiSwitch-thumb': {
                              backgroundColor: 'white',
                            }
                          }}
                        />
                      }
                      label="Visible in Member Directory"
                      sx={{ color: 'white' }}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }} display="block">
                      When enabled, this member will be visible to other members in the directory
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end', 
                      gap: 2, 
                      position: 'relative',
                      zIndex: 2  
                    }}>
                      <Button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        variant="outlined"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="button" 
                        disabled={isSaving}
                        variant="contained"
                        onClick={() => {
                          handleSubmit((data) => {
                            onSubmit(data);
                          })();
                        }}
                        sx={{
                          background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                          boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                          position: 'relative',  
                          '&:hover': {
                            background: 'linear-gradient(45deg, #303f9f 30%, #3f51b5 90%)',
                            cursor: 'pointer'  
                          }
                        }}
                      >
                        {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper 
            elevation={2} 
            sx={{ 
              borderRadius: 2,
              overflow: 'hidden',
              borderLeft: '4px solid #009688',
              mb: 4
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={selectedTab} 
                onChange={handleTabChange} 
                variant="fullWidth"
                sx={{ 
                  '& .MuiTab-root': { py: 2 },
                  '& .Mui-selected': {
                    color: '#009688',
                    fontWeight: 'medium'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#009688',
                  }
                }}
              >
                <Tab 
                  label="Payment History" 
                  icon={<CreditCardIcon />} 
                  iconPosition="start"
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AttachMoneyIcon sx={{ mr: 1 }} />
                      Debts Due
                      {totalActiveDebts > 0 && (
                        <Chip 
                          size="small" 
                          label={formatCurrency(totalActiveDebts)}
                          sx={{ ml: 1, bgcolor: 'error.light', color: 'white' }}
                        />
                      )}
                    </Box>
                  }
                />
                <Tab
                  label="Send Money"
                  icon={<MonetizationOnIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            <Box role="tabpanel" hidden={selectedTab !== 0}>
              {selectedTab === 0 && (
                <>
                  <Box 
                    sx={{ 
                      px: 3, 
                      py: 2, 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      background: 'linear-gradient(to right, rgba(0,150,136,0.05) 0%, rgba(255,255,255,0) 100%)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h6" fontWeight="medium" color="#009688">
                      Payment History
                    </Typography>
                    <Button
                      onClick={() => setPaymentModalOpen(true)}
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)',
                        boxShadow: '0 4px 6px rgba(0, 150, 136, 0.25)',
                      }}
                    >
                      Add Payment
                    </Button>
                  </Box>
                  {member.payments && member.payments.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ bgcolor: 'rgba(0,150,136,0.03)' }}>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Description</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {member.payments.map((payment, index) => (
                            <TableRow 
                              key={index}
                              sx={{ 
                                '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                                '&:hover': { bgcolor: 'rgba(0,150,136,0.08)' }
                              }}
                            >
                              <TableCell>{formatDate(payment.date)}</TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 'medium', color: '#009688' }}>
                                  ${payment.amount.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {payment.method.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </TableCell>
                              <TableCell>{payment.description || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>No payment records found.</Typography>
                      <Button
                        onClick={() => setPaymentModalOpen(true)}
                        variant="outlined"
                        startIcon={<PaymentIcon />}
                        color="primary"
                      >
                        Record First Payment
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Box role="tabpanel" hidden={selectedTab !== 1}>
              {selectedTab === 1 && (
                <>
                  <Box 
                    sx={{ 
                      px: 3, 
                      py: 2, 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      background: 'linear-gradient(to right, rgba(244,67,54,0.05) 0%, rgba(255,255,255,0) 100%)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h6" fontWeight="medium" color="error.main">
                      Outstanding Debts
                    </Typography>
                    <Button
                      onClick={() => setDebtDialogOpen(true)}
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
                        boxShadow: '0 4px 6px rgba(244, 67, 54, 0.25)',
                      }}
                    >
                      Add Debt
                    </Button>
                  </Box>
                  
                  {loadingDebts ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : debts && debts.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ bgcolor: 'rgba(244,67,54,0.03)' }}>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {debts.map((debt) => (
                            <TableRow 
                              key={debt._id}
                              sx={{ 
                                '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                                '&:hover': { bgcolor: 'rgba(244,67,54,0.08)' },
                                ...(debt.status === 'resolved' && {
                                  opacity: 0.7,
                                  bgcolor: 'rgba(0,0,0,0.04)'
                                })
                              }}
                            >
                              <TableCell>
                                <Typography sx={{ textTransform: 'capitalize' }}>
                                  {debt.category.replace(/_/g, ' ')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ 
                                  fontWeight: 'medium', 
                                  color: debt.status === 'active' ? 'error.main' : 'text.secondary'
                                }}>
                                  {formatCurrency(debt.amount || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell>{formatDate(debt.dueDate)}</TableCell>
                              <TableCell>
                                <Typography 
                                  sx={{ 
                                    maxWidth: 200, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                  }}
                                  title={debt.description || 'No description'}
                                >
                                  {debt.description || 'No description'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={debt.status === 'active' ? 'Active' : 'Resolved'}
                                  size="small"
                                  color={debt.status === 'active' ? 'error' : 'success'}
                                  sx={{ 
                                    fontWeight: 'medium',
                                    textTransform: 'uppercase',
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                  {debt.status === 'active' && (
                                    <MuiTooltip title="Make Payment">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenDebtPaymentDialog(debt)}
                                        sx={{
                                          color: 'primary.main',
                                        }}
                                      >
                                        <PaymentIcon />
                                      </IconButton>
                                    </MuiTooltip>
                                  )}
                                  <MuiTooltip title={debt.status === 'active' ? 'Mark as Resolved' : 'Mark as Active'}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleDebtStatus(debt._id, debt.status)}
                                      sx={{
                                        color: debt.status === 'active' ? 'success.main' : 'warning.main',
                                      }}
                                    >
                                      {debt.status === 'active' ? <CheckCircleIcon /> : <WarningIcon />}
                                    </IconButton>
                                  </MuiTooltip>
                                  <MuiTooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteDebt(debt._id)}
                                      color="error"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </MuiTooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>No debts found for this member.</Typography>
                      <Button
                        onClick={() => setDebtDialogOpen(true)}
                        variant="outlined"
                        startIcon={<AttachMoneyIcon />}
                        color="error"
                      >
                        Add First Debt
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
            
            <Box role="tabpanel" hidden={selectedTab !== 2}>
              {selectedTab === 2 && (
                <>
                  <Box 
                    sx={{ 
                      px: 3, 
                      py: 2, 
                      borderBottom: 1, 
                      borderColor: 'divider',
                      background: 'linear-gradient(to right, rgba(63,81,181,0.05) 0%, rgba(255,255,255,0) 100%)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Typography variant="h6" fontWeight="medium" color="primary.main">
                      Send Money to Member
                    </Typography>
                    <Button
                      onClick={() => setTransactionDialogOpen(true)}
                      variant="contained"
                      startIcon={<MonetizationOnIcon />}
                      sx={{
                        background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                        boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                      }}
                    >
                      Send Money
                    </Button>
                  </Box>
                  
                  {loadingTransactions ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <CircularProgress size={40} />
                    </Box>
                  ) : transactions && transactions.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead sx={{ bgcolor: 'rgba(63,81,181,0.03)' }}>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {transactions.map((transaction) => (
                            <TableRow 
                              key={transaction._id}
                              sx={{ 
                                '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                                '&:hover': { bgcolor: 'rgba(63,81,181,0.08)' }
                              }}
                            >
                              <TableCell>{formatDate(transaction.date)}</TableCell>
                              <TableCell>
                                <Typography sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                                  {formatCurrency(transaction.amount || 0)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography sx={{ textTransform: 'capitalize' }}>
                                  {transaction.reason?.replace(/_/g, ' ') || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  sx={{ 
                                    maxWidth: 200, 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    whiteSpace: 'nowrap' 
                                  }}
                                  title={transaction.description || 'No description'}
                                >
                                  {transaction.description || 'No description'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={transaction.status || 'completed'}
                                  size="small"
                                  color="primary"
                                  sx={{ 
                                    fontWeight: 'medium',
                                    textTransform: 'uppercase',
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>No transaction history found for this member.</Typography>
                      <Button
                        onClick={() => setTransactionDialogOpen(true)}
                        variant="outlined"
                        startIcon={<MonetizationOnIcon />}
                        color="primary"
                      >
                        Send First Transaction
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>
          
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #673AB7 0%, #9575CD 100%)',
                  color: 'white'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>Membership Status</Typography>
                    <Typography variant="h4" sx={{ my: 1, fontWeight: 'medium' }}>
                      {member.status?.toUpperCase()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Member since {formatDate(member.joinDate)}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white'
                    }}
                  >
                    <BadgeIcon />
                  </Avatar>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  borderRadius: 2,
                  height: '100%',
                  background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
                  color: 'white'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>Total Payments</Typography>
                    <Typography variant="h4" sx={{ my: 1, fontWeight: 'medium' }}>
                      ${member.payments?.reduce((sum, p) => sum + (p.amount || 0), 0).toFixed(2) || '0.00'}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {member.payments?.length || 0} payments recorded
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white'
                    }}
                  >
                    <CreditCardIcon />
                  </Avatar>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        aria-labelledby="payment-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          id="payment-dialog-title"
          sx={{
            bgcolor: 'rgba(0, 150, 136, 0.08)',
            borderBottom: '1px solid rgba(0, 150, 136, 0.2)'
          }}
        >
          <Typography sx={{ display: 'flex', alignItems: 'center', color: '#009688' }}>
            <PaymentIcon sx={{ mr: 1 }} /> Add Payment
          </Typography>
        </DialogTitle>
        <Box component="form" onSubmit={paymentForm.handleSubmit(handlePaymentSubmit)}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ step: "0.01", min: "0" }}
                  {...paymentForm.register('amount', { required: 'Amount is required' })}
                  error={!!paymentForm.formState.errors.amount}
                  helperText={paymentForm.formState.errors.amount?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="payment-method-label">Payment Method</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    id="paymentMethod"
                    label="Payment Method"
                    defaultValue="credit_card"
                    {...paymentForm.register('method')}
                  >
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="paypal">PayPal</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  placeholder="e.g., Membership fee for 2023"
                  {...paymentForm.register('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(0, 150, 136, 0.03)' }}>
            <Button
              onClick={() => setPaymentModalOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)',
                boxShadow: '0 4px 6px rgba(0, 150, 136, 0.25)',
              }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Add Payment'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      
      <Dialog
        open={debtDialogOpen}
        onClose={() => setDebtDialogOpen(false)}
        aria-labelledby="debt-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          id="debt-dialog-title"
          sx={{
            bgcolor: 'rgba(244, 67, 54, 0.08)',
            borderBottom: '1px solid rgba(244, 67, 54, 0.2)'
          }}
        >
          <Typography sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
            <AttachMoneyIcon sx={{ mr: 1 }} /> Add Outstanding Debt
          </Typography>
        </DialogTitle>
        <Box component="form" onSubmit={debtForm.handleSubmit(handleDebtSubmit)}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ step: "0.01", min: "0" }}
                  {...debtForm.register('amount', { required: 'Amount is required' })}
                  error={!!debtForm.formState.errors.amount}
                  helperText={debtForm.formState.errors.amount?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="debt-category-label">Category</InputLabel>
                  <Select
                    labelId="debt-category-label"
                    id="debtCategory"
                    label="Category"
                    defaultValue="dues"
                    {...debtForm.register('category')}
                  >
                    <MenuItem value="dues">Membership Dues</MenuItem>
                    <MenuItem value="fees">Administrative Fees</MenuItem>
                    <MenuItem value="event_fee">Event Fee</MenuItem>
                    <MenuItem value="late_payment">Late Payment Penalty</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  {...debtForm.register('dueDate')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  placeholder="e.g., Outstanding annual membership fee"
                  {...debtForm.register('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(244, 67, 54, 0.03)' }}>
            <Button
              onClick={() => setDebtDialogOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              variant="contained"
              color="error"
              sx={{
                background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
                boxShadow: '0 4px 6px rgba(244, 67, 54, 0.25)',
              }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Add Debt'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={debtPaymentDialog.open}
        onClose={() => setDebtPaymentDialog({ open: false, debt: null })}
        aria-labelledby="debt-payment-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          id="debt-payment-dialog-title"
          sx={{
            bgcolor: 'rgba(0, 150, 136, 0.08)',
            borderBottom: '1px solid rgba(0, 150, 136, 0.2)'
          }}
        >
          <Typography sx={{ display: 'flex', alignItems: 'center', color: '#009688' }}>
            <PaymentIcon sx={{ mr: 1 }} /> Pay Debt: {debtPaymentDialog.debt?.category?.replace(/_/g, ' ')}
          </Typography>
        </DialogTitle>
        <Box component="form" onSubmit={debtPaymentForm.handleSubmit(handleDebtPaymentSubmit)}>
          <DialogContent>
            {debtPaymentDialog.debt && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Debt Information
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Balance:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="error.main">
                    {formatCurrency(debtPaymentDialog.debt.amount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Due Date:
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(debtPaymentDialog.debt.dueDate)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {debtPaymentDialog.debt.description}
                </Typography>
                <Divider sx={{ my: 2 }} />
              </Box>
            )}
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Payment Amount"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ step: "0.01", min: "0.01" }}
                  {...debtPaymentForm.register('amount', { 
                    required: 'Amount is required',
                    validate: value => 
                      parseFloat(value) > 0 || 'Amount must be greater than zero',
                  })}
                  error={!!debtPaymentForm.formState.errors.amount}
                  helperText={debtPaymentForm.formState.errors.amount?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="payment-method-label">Payment Method</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    id="paymentMethod"
                    label="Payment Method"
                    defaultValue="credit_card"
                    {...debtPaymentForm.register('method')}
                  >
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="paypal">PayPal</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  placeholder="e.g., Partial payment for outstanding dues"
                  {...debtPaymentForm.register('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(0, 150, 136, 0.03)' }}>
            <Button
              onClick={() => setDebtPaymentDialog({ open: false, debt: null })}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #009688 30%, #4DB6AC 90%)',
                boxShadow: '0 4px 6px rgba(0, 150, 136, 0.25)',
              }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Apply Payment'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      
      <Dialog
        open={transactionDialogOpen}
        onClose={() => setTransactionDialogOpen(false)}
        aria-labelledby="transaction-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          id="transaction-dialog-title"
          sx={{
            bgcolor: 'rgba(63, 81, 181, 0.08)',
            borderBottom: '1px solid rgba(63, 81, 181, 0.2)'
          }}
        >
          <Typography sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
            <MonetizationOnIcon sx={{ mr: 1 }} /> Send Money to {userData?.firstName} {userData?.lastName}
          </Typography>
        </DialogTitle>
        <Box component="form" onSubmit={transactionForm.handleSubmit(handleTransactionSubmit)}>
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ step: "0.01", min: "0" }}
                  {...transactionForm.register('amount', { required: 'Amount is required' })}
                  error={!!transactionForm.formState.errors.amount}
                  helperText={transactionForm.formState.errors.amount?.message}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="transaction-reason-label">Reason</InputLabel>
                  <Select
                    labelId="transaction-reason-label"
                    id="transactionReason"
                    label="Reason"
                    defaultValue="refund"
                    {...transactionForm.register('reason')}
                  >
                    <MenuItem value="refund">Refund</MenuItem>
                    <MenuItem value="award">Award / Incentive</MenuItem>
                    <MenuItem value="expense_reimbursement">Expense Reimbursement</MenuItem>
                    <MenuItem value="payment_correction">Payment Correction</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  placeholder="e.g., Refund for overpayment"
                  {...transactionForm.register('description')}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(63, 81, 181, 0.03)' }}>
            <Button
              onClick={() => setTransactionDialogOpen(false)}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
              }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Send Money'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      
      <Dialog
        open={resetPasswordDialog.open}
        onClose={handleCloseResetDialog}
        aria-labelledby="reset-password-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          id="reset-password-dialog-title"
          sx={{
            bgcolor: 'rgba(63, 81, 181, 0.08)',
            borderBottom: '1px solid rgba(63, 81, 181, 0.2)'
          }}
        >
          <Typography sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
            <LockResetIcon sx={{ mr: 1 }} /> Reset Member Password
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {resetPasswordDialog.success ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
              <Typography variant="h6" color="success.main" gutterBottom>
                Password Reset Successful
              </Typography>
              
              {resetPasswordDialog.tempPassword ? (
                <>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    A new password has been sent to {resetPasswordDialog.email}
                  </Typography>
                  
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mb: 2, 
                      mt: 2,
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'rgba(33, 150, 243, 0.1)'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                      Temporary password:
                    </Typography>
                    <Box sx={{ 
                      position: 'relative', 
                      display: 'flex',
                      alignItems: 'center', 
                      bgcolor: 'background.paper',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 1,
                      pl: 2,
                      width: '100%'
                    }}>
                      <InputBase
                        value={resetPasswordDialog.tempPassword}
                        readOnly
                        type={passwordVisibility ? 'text' : 'password'}
                        sx={{ 
                          flexGrow: 1,
                          fontFamily: 'monospace',
                          fontSize: '1rem'
                        }}
                      />
                      <Box sx={{ display: 'flex' }}>
                        <ClickAwayListener onClickAway={() => setCopyTooltip('Copy to clipboard')}>
                          <MuiTooltip title={copyTooltip} placement="top">
                            <IconButton onClick={copyPasswordToClipboard} size="small">
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </MuiTooltip>
                        </ClickAwayListener>
                        
                        <IconButton
                          onClick={togglePasswordVisibility}
                          size="small"
                        >
                          {passwordVisibility ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Box>
                    </Box>
                  </Alert>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                    The member will be required to change this password on their next login.
                  </Typography>
                </>
              ) : (
                <Typography variant="body1">
                  A new password has been sent to {userData?.email}
                </Typography>
              )}
            </Box>
          ) : resetPasswordDialog.error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetPasswordDialog.error}
            </Alert>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to reset the password for:
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                {userData?.firstName} {userData?.lastName}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Email: <strong>{userData?.email}</strong>
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will generate a new random password and send it to the member's email address.
                The member will be required to change their password upon next login.
              </Alert>
            </>
          )}
        </DialogContent>
        {!resetPasswordDialog.success ? (
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(63, 81, 181, 0.03)' }}>
            <Button
              onClick={handleCloseResetDialog}
              variant="outlined"
              disabled={resetPasswordDialog.loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordDialog.loading}
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
              }}
            >
              {resetPasswordDialog.loading ? (
                <CircularProgress size={24} />
              ) : (
                'Reset Password'
              )}
            </Button>
          </DialogActions>
        ) : (
          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'rgba(63, 81, 181, 0.03)' }}>
            <Button
              onClick={handleCloseResetDialog}
              variant="contained"
              sx={{
                background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
              }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
      
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Select Report Type
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleSelectPrintReport('all')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#3f51b5' }} />
          Complete Financial Report
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('payments')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#009688' }} />
          Payment History Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('debts')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#f44336' }} />
          Outstanding Debts Only
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('transactions')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: '#ff9800' }} />
          Transactions Only
        </MenuItem>
      </Menu>
      
      <Dialog
        open={printDialogOpen}
        onClose={() => !isPrinting && setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrintIcon sx={{ mr: 1 }} />
            Financial Report Preview
          </Box>
          {!isPrinting && (
            <IconButton
              onClick={() => setPrintDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '70vh'
        }}>
          <Box sx={{ my: 2, width: '100%', overflow: 'auto' }}>
            <TransactionReport
              ref={transactionReportRef}
              member={member}
              transactions={transactions}
              debts={debts}
              payments={member.payments}
              tenant={tenant}
              reportType={printReportType}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintReport}
            disabled={isPrinting}
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            color="primary"
          >
            {isPrinting ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isPrinting}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 4,
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">
            Generating PDF...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please wait, this may take a moment
          </Typography>
        </Box>
      </Backdrop>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem'
            },
            boxShadow: 3,
            borderRadius: 2,
            ...(snackbar.severity === 'success' && {
              bgcolor: '#43a047',
              color: 'white'
            })
          }}
          icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : undefined}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MemberDetailPage;
